import { GoogleFontMap, GoogleFontResponse, googleFonts } from '@typedream/core';
import { debounce } from 'lodash';
import { DreamEditor } from '@typedream/dream-editor/src/types';
import { useEffect, useMemo, useState } from 'react';
import { getFontListFromEditor } from './getFontListFromEditor';
import { fetchFontData } from './fetchFontData';

export function useLoadFonts(editor: DreamEditor, iframeRef?: HTMLIFrameElement) {
  const [detectedFonts, setDetectedFonts] = useState<GoogleFontMap>({});
  const fontsList = useMemo(() => getFontListFromEditor(editor.children), [editor.children]);

  useEffect(() => {
    const getFonts = async () => {
      const fontDataResponse = await fetchFontData(fontsList);
      const fontData: GoogleFontResponse = await fontDataResponse.json();
      const { items } = fontData;

      if (items) {
        items.forEach((font) => {
          if (font.family) {
            setDetectedFonts((prevState) => ({
              ...prevState,
              [font.family]: font,
            }));
          }
        });
      }
    };

    const getFontsDebounced = debounce(getFonts, 750);

    getFontsDebounced();
  }, [fontsList]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Load font maps and return as a font family string for webfontloader
      let fontList: GoogleFontMap = googleFonts;

      if (detectedFonts) {
        fontList = detectedFonts;
      } else {
        fontList = googleFonts;
      }

      const fontMap = Object.keys(fontList);
      const fontsToLoad = fontMap.map((font) => {
        const currentFont = fontList[font];
        return `${currentFont.family}:${currentFont.variants.join(',')}`;
      });

      import('webfontloader').then((WebFont) => {
        if (fontsToLoad?.length > 0) {
          WebFont.load({
            google: {
              families: fontsToLoad,
            },
            context: iframeRef?.contentWindow ?? undefined,
          });
        }
      });
    }
  }, [detectedFonts]);
}
